import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { getPlatformColor } from "../PlatformBadge";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

interface PlatformRoasChartProps {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function PlatformRoasChart({ isEditMode, onRemove, onResize, onResetSize }: PlatformRoasChartProps) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey, sourceType } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery<{ platforms: string[]; data: Array<Record<string, string | number>> }>({
    queryKey: ["platform-roas-trend", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("platform-daily-trend", params));
      if (!res.ok) throw new Error("Failed to fetch platform trend data");
      return res.json();
    },
    enabled: sourceType === "unified",
  });

  const platforms = data?.platforms || [];
  const chartData = (data?.data || []).map(d => ({
    ...d,
    date: (d.date as string).slice(5),
  }));

  const platformNames: Record<string, string> = {
    meta: "Meta",
    tiktok: "TikTok",
    google: "Google",
    pinterest: "Pinterest",
  };

  return (
    <DashboardWidget
      title="ROAS by Platform"
      widgetType="chart"
      isEditMode={isEditMode}
      isLoading={isLoading}
      error={error?.message ?? null}
      onRetry={() => refetch()}
      onRemove={onRemove}
      onResize={onResize}
      onResetSize={onResetSize}
      platformSlug="unified"
    >
      <div className="p-4 h-full flex flex-col" data-testid="chart-platform-roas">
        {chartData.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-sm" style={{ color: "#5F6D82" }}>
            No platform data available
          </div>
        ) : (
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tickFormatter={(v) => `${v}x`} tick={{ fontSize: 10 }} width={40} />
                <Tooltip
                  formatter={(value: number, name: string) => [`${value.toFixed(2)}x`, platformNames[name] || name]}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  labelStyle={{ fontSize: 11, fontWeight: 600 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11 }}
                  formatter={(value: string) => platformNames[value] || value}
                />
                {platforms.map(slug => (
                  <Line
                    key={slug}
                    type="monotone"
                    dataKey={slug}
                    stroke={getPlatformColor(slug)}
                    strokeWidth={2}
                    dot={false}
                    name={slug}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </DashboardWidget>
  );
}
